import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DataTable, TableCellEllipsis } from "../ui";
import { MDBContainer, MDBTooltip } from "mdb-react-ui-kit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faCheck,
  faCopy,
} from "@fortawesome/free-solid-svg-icons";
import Toolbar from "../Toolbar";
import Actionbar from "../Actionbar";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { api } from "../../services";
import PriceDisplay from "../PriceDisplay";
import OrderStatsCard from "../OrdersReportComponents/OrderStatsCard";
import ShipmentDocumentButtons from "./ShipmentDocumentButtons";
import ShipmentDetailsDrawer from "./ShipmentDetailsDrawer";
import ConfirmModal from "../Modals/ConfirmModal";
import {
  fetchTrackHubReports,
  fetchTrackHubReportsExport,
  fetchTrackHubBranches,
  cancelTrackHubShipment,
} from "../../services/trackHubApi";
import { resolveDateLocale } from "../../utils/formatDateTime";
import { usePermission } from "../../hooks/usePermission";
import {
  RBAC_MODULES,
  PERMISSION_ACTIONS,
} from "../../config/routePermissions";
import "./trackhub-reports.css";
import CourierInfoCell from "./CourierInfoCell";

const PAYMENT_POLL_MS = 10000;

const STATUS_CLASS = {
  Delivered: "trackhub-reports-status--delivered",
  "In Transit": "trackhub-reports-status--in-transit",
  Assigned: "trackhub-reports-status--assigned",
  Pending: "trackhub-reports-status--pending",
  NEW: "trackhub-reports-status--pending",
  "Pending Payment": "trackhub-reports-status--pending",
  Scheduled: "trackhub-reports-status--pending",
  Cancelled: "trackhub-reports-status--cancelled",
};

/** Order-less Manual Shipments from Create Shipment (not order-linked TrackHub). */
function isManualShipmentRow(row) {
  if (!row) return false;
  if (String(row.origin || "").toLowerCase() === "manual_standalone") return true;
  const platform = String(row.platform || row.orderSource || "").toUpperCase();
  if (platform === "MANUAL" && !row.orderId) return true;
  if (!row.orderId && String(row.orderRef || "").toUpperCase().startsWith("MANUAL-")) {
    return true;
  }
  return false;
}

/** Scheduled row not yet dispatched to the carrier (mirrors backend isLocalScheduledDispatchHold). */
function isLocalScheduledDispatchHold(row, now = Date.now()) {
  if (!row) return false;
  if (row.dispatchStatus === "succeeded" || row.shipmentId) return false;
  if (row.paymentStatus === "pending_payment" || row.status === "Pending Payment") {
    return false;
  }
  if (row.paymentStatus === "paid" && row.coveredByBusiness !== true) {
    return false;
  }
  const scheduledAt = row.scheduledAt ? new Date(row.scheduledAt).getTime() : NaN;
  if (Number.isFinite(scheduledAt) && scheduledAt > now) {
    return true;
  }
  if (String(row.status || "").trim() === "Scheduled") {
    return true;
  }
  return false;
}

function resolveCancelShipmentId(row) {
  const pending =
    row?.paymentStatus === "pending_payment" || row?.status === "Pending Payment";
  if (pending || isLocalScheduledDispatchHold(row)) {
    return row?._id;
  }
  return row?.shipmentId;
}

/**
 * null = Cancel enabled.
 * Otherwise a shipmentsPage i18n key suffix under cancelDisabled*.
 */
function getCancelDisabledReason(row) {
  if (!isManualShipmentRow(row)) return "orderLinked";

  if (row?.paymentStatus === "pending_payment" || row?.status === "Pending Payment") {
    return null;
  }

  if (isLocalScheduledDispatchHold(row)) {
    return null;
  }

  const th = String(row.trackhubStatus || "").toUpperCase();
  const ui = String(row.status || "").toUpperCase();

  if (th === "DELIVERED" || ui === "DELIVERED") {
    return "delivered";
  }

  if (
    ["FAILED", "RETURNED", "CANCELLED"].includes(th) ||
    ui === "CANCELLED"
  ) {
    return "status";
  }

  if (
    [
      "ASSIGNED",
      "AT_PICKUP",
      "PICKED_UP",
      "IN_TRANSIT",
      "AT_DROPOFF",
    ].includes(th) ||
    ["ASSIGNED", "IN TRANSIT"].includes(ui)
  ) {
    return "courierAssigned";
  }

  if (row?.paymentStatus === "paid" && row?.coveredByBusiness !== true) {
    return "paid";
  }

  if (!row?.shipmentId) {
    return "missingId";
  }

  if (
    th === "CREATED" ||
    th === "NEW" ||
    !th ||
    ui === "PENDING" ||
    ui === "NEW" ||
    ui === "SCHEDULED"
  ) {
    return null;
  }

  return "status";
}

function statusLabelKey(status) {
  const normalized = String(status || "").trim().toLowerCase();
  if (normalized === "delivered") return "statusDelivered";
  if (normalized === "in transit" || normalized === "in_transit") return "statusInTransit";
  if (normalized === "assigned") return "statusAssigned";
  if (normalized === "pending payment") return "statusPendingPayment";
  if (normalized === "scheduled") return "statusScheduled";
  if (normalized === "pending" || normalized === "new") return "statusPending";
  if (normalized === "cancelled" || normalized === "canceled") return "statusCancelled";
  return null;
}

function StatusBadge({ status, label }) {
  const cls = STATUS_CLASS[status] || "trackhub-reports-status--pending";
  return (
    <span className={`trackhub-reports-status ${cls}`}>
      {label || status}
    </span>
  );
}

function ShipmentIdCell({ shipmentId, copyLabel, copiedLabel }) {
  const [copied, setCopied] = useState(false);

  if (!shipmentId) {
    return <span className="trackhub-reports-mono">—</span>;
  }

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(shipmentId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy shipment id", err);
    }
  };

  return (
    <div className="trackhub-reports-id-cell" dir="ltr" onClick={(e) => e.stopPropagation()}>
      <TableCellEllipsis title={shipmentId}>{shipmentId}</TableCellEllipsis>
      <button
        type="button"
        className="trackhub-reports-copy-btn"
        onClick={handleCopy}
        title={copied ? copiedLabel : copyLabel}
        aria-label={copied ? copiedLabel : copyLabel}
      >
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
      </button>
    </div>
  );
}

function defaultLast30Days() {
  const to = new Date();
  to.setHours(0, 0, 0, 0);
  const from = new Date(to);
  from.setDate(from.getDate() - 29); // inclusive Last 30 days (matches DatePicker preset)
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return { dateFrom: fmt(from), dateTo: fmt(to) };
}

function formatOrderSource(source, t) {
  const s = String(source || "").toUpperCase();
  if (!s || s === "MANUAL" || s === "NA") {
    return t("shipmentsPage.na");
  }
  if (s.includes("CATALOG") || s === "DIGITAL_CATALOG") {
    return t("shipmentsPage.orderSourceDigitalCatalog");
  }
  if (
    s.includes("CHAT") ||
    s.includes("ORDER_AGENT") ||
    s === "WHATSAPP_ORDER_AGENT"
  ) {
    return t("shipmentsPage.orderSourceOrderAgent");
  }
  return source || t("shipmentsPage.na");
}

function TrackHubReportsPage() {
  const { t, i18n } = useTranslation("translation");
  const navigate = useNavigate();
  const isArabic =
    String(i18n.language || "").toLowerCase().startsWith("ar") ||
    i18n.dir() === "rtl";
  const dateLocale = resolveDateLocale(i18n.language);
  const { can } = usePermission();
  const canExport = can(RBAC_MODULES.SHIPMENTS, PERMISSION_ACTIONS.EXPORT);
  const canCreate = can(RBAC_MODULES.SHIPMENTS, PERMISSION_ACTIONS.CREATE);
  const canCancel = can(RBAC_MODULES.SHIPMENTS, PERMISSION_ACTIONS.CANCEL);

  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [filterOptions, setFilterOptions] = useState({ carriers: [] });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({});
  const [dateRange, setDateRange] = useState(defaultLast30Days);
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedCarrier, setSelectedCarrier] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [storesOptions, setStoresOptions] = useState([]);
  const [branchesOptions, setBranchesOptions] = useState([]);
  const [manualBranches, setManualBranches] = useState([]);
  const [manualBranchesLoading, setManualBranchesLoading] = useState(false);
  const [cancelTarget, setCancelTarget] = useState(null);
  const [detailRow, setDetailRow] = useState(null);
  const [cancelling, setCancelling] = useState(false);
  const loadGen = useRef(0);

  const hasManualBranches = manualBranches.length > 0;
  const hasPendingPayment = rows.some(
    (row) => row?.paymentStatus === "pending_payment" || row?.status === "Pending Payment"
  );

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadStores = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const res = await api.get("/stores/my", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const stores = res.data?.stores || [];
      setStoresOptions(
        stores.map((s) => ({
          _id: s._id || s.id,
          name: s.name || s.name_en || s.displayName || "",
          name_en: s.name || s.name_en || s.displayName || "",
          name_ar: s.name_ar || s.displayNameAr || s.name || "",
          branches: s.branches || [],
        }))
      );
    } catch (error) {
      console.error("Failed to load stores for Shipments", error);
    }
  }, []);

  const loadReports = useCallback(async ({ silent = false } = {}) => {
    const gen = silent ? loadGen.current : (loadGen.current += 1);
    if (!silent) setLoading(true);
    try {
      const result = await fetchTrackHubReports({
        page,
        limit,
        status: selectedStatus,
        carrier: selectedCarrier,
        storeId: selectedStoreId || undefined,
        branchId: selectedBranchId || undefined,
        dateFrom: dateRange.dateFrom || undefined,
        dateTo: dateRange.dateTo || undefined,
        search: debouncedSearch || undefined,
        sortBy: sortColumn || "createdAt",
        sortOrder: sortDirection || "desc",
      });
      if (loadGen.current !== gen) return;
      setRows(result.data || []);
      setSummary(result.summary || {});
      setFilterOptions(result.filterOptions || { carriers: [] });
      setPagination(result.pagination || {});
    } catch (error) {
      if (loadGen.current !== gen) return;
      console.error("Failed to load Shipments", error);
      if (!silent) toast.error(t("shipmentsPage.loadError"));
    } finally {
      if (!silent && loadGen.current === gen) setLoading(false);
    }
  }, [
    page,
    limit,
    selectedStatus,
    selectedCarrier,
    selectedStoreId,
    selectedBranchId,
    dateRange.dateFrom,
    dateRange.dateTo,
    debouncedSearch,
    sortColumn,
    sortDirection,
    t,
  ]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  const loadManualBranches = useCallback(async () => {
    setManualBranchesLoading(true);
    try {
      const enabled = [];
      let pageNum = 1;
      let hasNext = true;
      while (hasNext && pageNum <= 20) {
        const result = await fetchTrackHubBranches({ limit: 100, page: pageNum });
        const rowsData = result.data || [];
        enabled.push(...rowsData.filter(
          (row) => row.allowManualShipment === true && row.isEnabled === true
        ));
        hasNext = Boolean(result.pagination?.hasNextPage);
        pageNum += 1;
      }
      setManualBranches(enabled);
    } catch (error) {
      console.error("Failed to load manual shipment branches", error);
      setManualBranches([]);
    } finally {
      setManualBranchesLoading(false);
    }
  }, []);

  useEffect(() => {
    loadManualBranches();
  }, [loadManualBranches]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  useEffect(() => {
    if (!hasPendingPayment) return undefined;
    const refresh = () => {
      if (document.hidden) return;
      loadReports({ silent: true });
    };
    const timer = window.setInterval(refresh, PAYMENT_POLL_MS);
    const onVisible = () => {
      if (!document.hidden) refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [hasPendingPayment, loadReports]);

  useEffect(() => {
    const store = storesOptions.find((s) => s._id === selectedStoreId);
    if (!store) {
      setBranchesOptions([]);
      return;
    }
    setBranchesOptions(
      (store.branches || []).map((b) => ({
        _id: b._id || b.id,
        name: b?.name?.en || b?.name || b?.displayName || "",
        name_en: b?.name?.en || b?.name || b?.displayName || "",
        name_ar: b?.name?.ar || b?.name_ar || b?.displayNameAr || b?.name || "",
      }))
    );
  }, [selectedStoreId, storesOptions]);

  const reportStatuses = useMemo(
    () => [
      { id: "ALL", name: t("all") },
      { id: "Delivered", name: t("shipmentsPage.statusDelivered") },
      { id: "In Transit", name: t("shipmentsPage.statusInTransit") },
      { id: "Assigned", name: t("shipmentsPage.statusAssigned") },
      { id: "Pending", name: t("shipmentsPage.statusPending") },
      { id: "Pending Payment", name: t("shipmentsPage.statusPendingPayment") },
      { id: "Scheduled", name: t("shipmentsPage.statusScheduled") },
      { id: "Cancelled", name: t("shipmentsPage.statusCancelled") },
    ],
    [t]
  );

  const carrierOptions = useMemo(
    () => [
      { _id: "ALL", name: t("all") },
      ...(filterOptions.carriers || []).map((carrier) => ({
        _id: carrier,
        name: carrier,
      })),
    ],
    [filterOptions.carriers, t]
  );

  const handleExport = async () => {
    if (!canExport) {
      toast.error(t("noPermission") || "No permission");
      return;
    }
    try {
      setIsExporting(true);
      const { blob, filename } = await fetchTrackHubReportsExport({
        status: selectedStatus,
        carrier: selectedCarrier,
        storeId: selectedStoreId || undefined,
        branchId: selectedBranchId || undefined,
        dateFrom: dateRange.dateFrom || undefined,
        dateTo: dateRange.dateTo || undefined,
        search: debouncedSearch || undefined,
        sortBy: sortColumn || "createdAt",
        sortOrder: sortDirection || "desc",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting shipments:", err);
      toast.error(t("shipmentsPage.exportError"));
    } finally {
      setIsExporting(false);
    }
  };

  const handleCancelShipment = async (row) => {
    const cancelId = resolveCancelShipmentId(row);
    if (!cancelId) {
      toast.error(t("shipmentsPage.cancelMissingId"));
      return;
    }
    setCancelling(true);
    try {
      await cancelTrackHubShipment(cancelId);
      toast.success(t("shipmentsPage.cancelSuccess"));
      setCancelTarget(null);
      await loadReports();
    } catch (error) {
      const status = error?.response?.status;
      const data = error?.response?.data || {};
      const preferAr = isArabic;
      if (
        status === 409 ||
        data.error_code === "CANCEL_NOT_ALLOWED_PAID" ||
        data.error_code === "CANCEL_NOT_ALLOWED_AFTER_ASSIGNMENT" ||
        data.error_code === "ALREADY_TERMINAL" ||
        data.error_code === "CANCEL_NOT_ALLOWED_FOR_ORDER_SHIPMENT"
      ) {
        toast.error(
          preferAr && data.message_ar
            ? data.message_ar
            : data.message || t("shipmentsPage.cancelAlreadyAssigned")
        );
      } else if (status === 502 || data.error_code === "CARRIER_CANCEL_FAILED") {
        toast.error(
          preferAr && data.message_ar
            ? data.message_ar
            : data.message || t("shipmentsPage.cancelCarrierError")
        );
      } else {
        toast.error(
          preferAr && data.message_ar
            ? data.message_ar
            : data.message || t("shipmentsPage.cancelError")
        );
      }
    } finally {
      setCancelling(false);
    }
  };

  const columns = [
    {
      id: "rowIndex",
      header: "#",
      minWidth: 56,
      width: 56,
      align: "center",
      headerAlign: "center",
      hideable: false,
      nowrap: true,
      className: "oh-table-index-col trackhub-reports-index-col",
      headerClassName: "oh-table-index-col trackhub-reports-index-col",
      render: (_row, index) => (page - 1) * limit + index + 1,
    },
    {
      id: "shipmentOrder",
      header: t("shipmentsPage.shipmentOrder"),
      minWidth: 180,
      render: (row) => (
        <div className="trackhub-reports-stack trackhub-reports-shipment-order">
          <ShipmentIdCell
            shipmentId={row.shipmentId || (row.status === "Pending Payment" ? row.orderRef : "")}
            copyLabel={t("shipmentsPage.copyShipmentId")}
            copiedLabel={t("shipmentsPage.shipmentIdCopied")}
          />
          <span className="trackhub-reports-stack__sub" dir="ltr">
            {row.orderRef || t("shipmentsPage.na")}
          </span>
        </div>
      ),
    },
    {
      id: "branchSource",
      header: t("shipmentsPage.branchAndSource"),
      minWidth: 160,
      render: (row) => (
        <div className="trackhub-reports-stack">
          <span>{row.branch || "—"}</span>
          <span className="trackhub-reports-stack__sub">
            {formatOrderSource(row.orderSource || row.origin, t)}
          </span>
        </div>
      ),
    },
    {
      id: "courier",
      header: t("shipmentsPage.courier"),
      minWidth: 160,
      render: (row) => (
        <div className="trackhub-reports-stack">
          <CourierInfoCell row={row} />
          <span className="trackhub-reports-stack__sub" dir="ltr">
            {row.deliveryId || "—"}
          </span>
        </div>
      ),
    },
    {
      id: "status",
      header: t("shipmentsPage.deliveryStatus"),
      minWidth: 150,
      render: (row) => {
        const key = statusLabelKey(row.status);
        return (
          <StatusBadge
            status={row.status}
            label={key ? t(`shipmentsPage.${key}`) : row.status}
          />
        );
      },
    },
    {
      id: "paymentStatus",
      header: t("shipmentsPage.paymentStatus"),
      minWidth: 150,
      render: (row) => {
        const customerShare = Number(row.customerShare);
        const paymentSkipped = row.coveredByBusiness === true
          && Number.isFinite(customerShare)
          && customerShare > 0;
        let label = "—";
        if (row.paymentStatus === "skipped" || paymentSkipped) {
          label = t("shipmentsPage.paymentSkipped");
        } else if (!row.paymentStatus) {
          return <span className="trackhub-reports-mono">—</span>;
        } else if (row.coveredByBusiness && row.paymentStatus === "paid") {
          label = t("shipmentsPage.coveredByBusiness");
        } else if (row.paymentStatus === "paid") {
          label = t("shipmentsPage.paid");
        } else if (row.paymentStatus === "expired") {
          label = t("shipmentsPage.paymentExpired");
        } else if (row.paymentStatus === "canceled") {
          label = t("shipmentsPage.paymentCanceled");
        } else if (
          row.paymentStatus === "pending_payment" ||
          row.status === "Pending Payment"
        ) {
          label = t("shipmentsPage.statusPendingPayment");
        } else if (!row.paymentStatus) {
          return <span className="trackhub-reports-mono">—</span>;
        }
        return <span className="trackhub-reports-status trackhub-reports-status--pending">{label}</span>;
      },
    },
    {
      id: "costSplit",
      header: t("shipmentsPage.costSplit"),
      minWidth: 172,
      className: "trackhub-reports-td-cost-split",
      render: (row) => {
        const business = row.restaurantShare != null ? Number(row.restaurantShare) : null;
        const customer = row.customerShare != null ? Number(row.customerShare) : null;
        const total =
          business != null && customer != null ? business + customer : row.price ?? 0;
        return (
          <div className="trackhub-reports-stack trackhub-reports-cost-split">
            <div className="trackhub-reports-cost-split__line trackhub-reports-cost-split__line--total">
              <span className="trackhub-reports-cost-split__label">
                {t("shipmentsPage.details.costSplitTotal")}
              </span>
              <span className="trackhub-reports-cost-split__amount">
                <PriceDisplay value={total} iconSize={12} />
              </span>
            </div>
            <div className="trackhub-reports-cost-split__line">
              <span className="trackhub-reports-cost-split__label">
                {t("shipmentsPage.details.businessPays")}
              </span>
              <span className="trackhub-reports-cost-split__amount">
                {business != null ? <PriceDisplay value={business} iconSize={12} /> : "—"}
              </span>
            </div>
            <div className="trackhub-reports-cost-split__line">
              <span className="trackhub-reports-cost-split__label">
                {t("shipmentsPage.details.customerPays")}
              </span>
              <span className="trackhub-reports-cost-split__amount">
                {customer != null ? <PriceDisplay value={customer} iconSize={12} /> : "—"}
              </span>
            </div>
          </div>
        );
      },
    },
    {
      id: "action",
      header: t("shipmentsPage.action"),
      minWidth: 220,
      align: "center",
      headerAlign: "center",
      hideable: false,
      render: (row) => {
        const uiStatus = String(row.status || "").toUpperCase();
        const thStatus = String(row.trackhubStatus || "").toUpperCase();
        const isCancelled =
          uiStatus === "CANCELLED" ||
          ["FAILED", "RETURNED", "CANCELLED"].includes(thStatus);
        const showCancel =
          canCancel && !isCancelled && isManualShipmentRow(row);
        const disabledReason = showCancel ? getCancelDisabledReason(row) : "status";
        const enabled = showCancel && disabledReason == null;
        const tooltip = enabled
          ? ""
          : t(
              `shipmentsPage.cancelDisabled.${disabledReason}`,
              disabledReason === "courierAssigned"
                ? "Shipment cannot be cancelled after a courier has been assigned."
                : disabledReason === "delivered"
                  ? "Delivered shipments cannot be cancelled."
                  : disabledReason === "paid"
                    ? "Paid shipments cannot be cancelled."
                    : "Shipment cannot be cancelled in its current status."
            );
        const payHref =
          row.paymentStatus === "pending_payment" && row.paymentLinkUrl
            ? row.paymentLinkUrl
            : row.invoiceUrl || "";
        const payText =
          row.paymentStatus === "paid" && row.invoiceUrl
            ? t("shipmentsPage.invoiceLink")
            : t("shipmentsPage.paymentLink");

        const cancelBtn = showCancel ? (
          <button
            type="button"
            className="trackhub-reports-cancel-btn"
            disabled={!enabled || cancelling}
            aria-disabled={!enabled || cancelling}
            style={!enabled || cancelling ? { pointerEvents: "none" } : undefined}
            onClick={() => {
              if (!enabled || cancelling) return;
              setCancelTarget(row);
            }}
          >
            <span>{t("shipmentsPage.cancel")}</span>
          </button>
        ) : null;

        return (
          <div
            className="trackhub-reports-actions"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              className="trackhub-reports-view-btn"
              onClick={() => setDetailRow(row)}
            >
              {t("shipmentsPage.view")}
            </button>
            {payHref ? (
              <a
                className="trackhub-reports-payment__link"
                href={payHref}
                target="_blank"
                rel="noopener noreferrer"
              >
                {payText}
              </a>
            ) : null}
            {row.labelUrl ? (
              <ShipmentDocumentButtons row={row} t={t} />
            ) : null}
            {cancelBtn && !enabled && tooltip ? (
              <MDBTooltip
                tag="span"
                title={tooltip}
                placement="top"
                wrapperProps={{ className: "trackhub-reports-cancel-wrap" }}
              >
                <span className="trackhub-reports-cancel-wrap">{cancelBtn}</span>
              </MDBTooltip>
            ) : (
              cancelBtn
            )}
          </div>
        );
      },
    },
  ];

  const exportPrimaryAction = canExport
    ? {
        label: isExporting
          ? t("shipmentsPage.exporting")
          : t("shipmentsPage.export"),
        onClick: handleExport,
        disabled: isExporting,
        icon: (
          <FontAwesomeIcon
            icon={faArrowUpRightFromSquare}
            style={{ transform: isArabic ? "scaleX(-1)" : "none" }}
          />
        ),
      }
    : undefined;

  return (
    <div className="trackhub-reports-page oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <h1 className="page-title-header">{t("shipmentsPage.title")}</h1>

        <div className="oh-page-section">
          <div className="oh-kpi-row oh-kpi-row--4">
            <OrderStatsCard
              title={t("shipmentsPage.totalShipments")}
              value={summary.totalShipments ?? summary.totalDeliveries ?? 0}
              loading={loading}
            />
            <OrderStatsCard
              title={t("shipmentsPage.delivered")}
              value={summary.delivered ?? summary.deliveredCount ?? 0}
              loading={loading}
            />
            <OrderStatsCard
              title={t("shipmentsPage.failed")}
              value={summary.failed ?? 0}
              loading={loading}
            />
            <OrderStatsCard
              title={t("shipmentsPage.returned")}
              value={summary.returned ?? 0}
              loading={loading}
            />
          </div>
        </div>

        {canCreate ? (
        <div className="oh-page-section">
          <Actionbar
            title={t("quickAction")}
            subtitle={t("manageYouWorkSpace")}
            firstBtnText={t("shipmentsPage.createShipment")}
            firstBtnIcon="plus"
            firstBtnClass="primary"
            firstBtnOnClick={() => navigate("/shipments/new")}
            disabledBtn1={!hasManualBranches || manualBranchesLoading}
            firstBtnTooltip={
              hasManualBranches
                ? ""
                : t("shipmentsPage.createShipmentDisabledTooltip")
            }
          />
        </div>
        ) : null}

        <div className="oh-page-section">
          <Toolbar
            toolbarVariant="shipments"
            hideIcons
            showSearch
            searchQuery={searchQuery}
            handleSearch={(e) => setSearchQuery(e.target.value)}
            searchPlaceHolder={t("shipmentsPage.searchPlaceholder")}
            extraDropdowns={[
              {
                type: "date-range",
                label: t("datePresets.customRange"),
                placeholder: t("datePresets.last30"),
                value: [
                  dateRange.dateFrom || null,
                  dateRange.dateTo || null,
                ],
                onChange: (val) => {
                  const [from, to] = val || [];
                  setDateRange({
                    dateFrom: from || "",
                    dateTo: to || "",
                  });
                  setPage(1);
                },
              },
              {
                label: t("shipmentsPage.brand"),
                placeholder: t("trackHubBranchesPage.allBrands"),
                options: storesOptions,
                selectedOption: selectedStoreId,
                showClear: true,
                backgroundColor: "#ffffff",
                handleSelect: (opt) => {
                  setSelectedStoreId(opt?._id || "");
                  setSelectedBranchId("");
                  setPage(1);
                },
              },
              {
                label: t("shipmentsPage.branch"),
                placeholder: selectedStoreId
                  ? t("trackHubBranchesPage.allBranches")
                  : t("shipmentsPage.selectBrandFirst"),
                options: branchesOptions,
                selectedOption: selectedBranchId,
                showClear: true,
                disabled: !selectedStoreId,
                backgroundColor: "#ffffff",
                handleSelect: (opt) => {
                  setSelectedBranchId(opt?._id || "");
                  setPage(1);
                },
              },
              {
                label: t("shipmentsPage.courier"),
                placeholder: t("shipmentsPage.courier"),
                options: carrierOptions,
                selectedOption: selectedCarrier || "ALL",
                showClear: false,
                backgroundColor: "#ffffff",
                handleSelect: (opt) => {
                  setSelectedCarrier(opt?._id || "ALL");
                  setPage(1);
                },
              },
              {
                label: t("shipmentsPage.status"),
                placeholder: t("shipmentsPage.status"),
                options: reportStatuses.map((s) => ({ _id: s.id, name: s.name })),
                selectedOption: selectedStatus || "ALL",
                showClear: false,
                backgroundColor: "#ffffff",
                handleSelect: (opt) => {
                  setSelectedStatus(opt?._id || "ALL");
                  setPage(1);
                },
              },
            ]}
          />
        </div>

        <div className="oh-page-section">
          <DataTable
            tableId="shipments-v2"
            title={t("shipmentsPage.tableTitle")}
            columns={columns}
            data={rows}
            loading={loading}
            emptyState={t("shipmentsPage.empty")}
            primaryAction={exportPrimaryAction}
            showColumnSettings
            tableMinWidth={1100}
            onRowClick={(row) => setDetailRow(row)}
            isRtl={isArabic}
            sort={{
              column: sortColumn,
              direction: sortDirection,
              onChange: ({ column, direction }) => {
                setSortColumn(column || "createdAt");
                setSortDirection(direction === "asc" ? "asc" : "desc");
                setPage(1);
              },
            }}
            pagination={{
              page,
              pageSize: limit,
              total: pagination.total || 0,
              totalPages: pagination.totalPages || 1,
              hasPrevPage: pagination.hasPrevPage,
              hasNextPage: pagination.hasNextPage,
              onPageChange: setPage,
              onPageSizeChange: (size) => {
                setLimit(size);
                setPage(1);
              },
              isRtl: isArabic,
            }}
          />
        </div>
      </MDBContainer>

      <ShipmentDetailsDrawer
        open={Boolean(detailRow)}
        row={detailRow}
        onClose={() => setDetailRow(null)}
        t={t}
        dateLocale={dateLocale}
        dir={isArabic ? "rtl" : "ltr"}
        language={i18n.language}
      />

      <ConfirmModal
        show={Boolean(cancelTarget)}
        onHide={() => {
          if (!cancelling) setCancelTarget(null);
        }}
        title={t("shipmentsPage.cancelConfirmTitle")}
        message={
          cancelTarget?.paymentStatus === "pending_payment" ||
          cancelTarget?.status === "Pending Payment"
            ? t("shipmentsPage.cancelPendingPaymentMessage", {
                id: cancelTarget?.orderRef || "",
              })
            : isLocalScheduledDispatchHold(cancelTarget)
              ? t("shipmentsPage.cancelScheduledMessage", {
                  id: cancelTarget?.orderRef || "",
                })
              : t("shipmentsPage.cancelConfirmMessage", {
                  id: cancelTarget?.shipmentId || "",
                })
        }
        action={() => handleCancelShipment(cancelTarget)}
      />
    </div>
  );
}

export default TrackHubReportsPage;
